import { clearRef, normalizeReferralCode, readRef, REF_PARAM, storeRef } from "@/lib/services/utm"
import { hostReferralCode } from "@/platform/bridge/use-native-bridge"
import { ANONYMOUS_VIEWER } from "@/platform/session/viewer"

const PROMO_REJECTIONS = ["alreadyPurchased", "existingLoans", "alreadyRedeemed", "selfReferral", "offerUnavailable", "notAvailable", "notFound", "generic"] as const

export type PromoRejection = (typeof PROMO_REJECTIONS)[number]

export const isTerminalReferralRejection = (reason: PromoRejection): boolean =>
  reason === "alreadyPurchased" || reason === "existingLoans" || reason === "alreadyRedeemed" || reason === "selfReferral"

export interface ReferralRejection {
  code: string
  reason: PromoRejection
  dropped?: boolean
}

export interface ReferralApplied {
  code: string
  amount: number
}

const NOTICE_TTL_MS = 24 * 60 * 60 * 1000

const SOURCE_KEY = "ref_source"

const TYPED_OWNER_KEY = "ref_typed_owner"

type ReferralCodeSource = "link" | "host" | "typed"

const REJECTION_KEY = "ref_rejection"

const APPLIED_KEY = "ref_applied"

const BANNER_DISMISSED_KEY = "ref_banner_dismissed"

const ORDER_SEEN_KEY = "ref_order_seen"

const HOST_SPENT_KEY = "ref_host_spent"

const HOST_SPENT_OWNER_KEY = "ref_host_spent_owner"

const CHANGE_EVENT = "nelo:referral-attribution-change"

let viewer = ANONYMOUS_VIEWER

const notify = () => {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(CHANGE_EVENT))
}

const read = (key: string): string | null => {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

const write = (key: string, value: string | null) => {
  try {
    if (value === null) localStorage.removeItem(key)
    else localStorage.setItem(key, value)
  } catch {
    return
  }
  notify()
}

export const readStoredReferralCode = (): string | null => {
  try {
    return readRef()
  } catch {
    return null
  }
}

export const storeReferralCode = (code: string, source: ReferralCodeSource = "link") => {
  const normalized = normalizeReferralCode(code)
  if (!normalized) return
  try {
    storeRef(normalized)
    localStorage.setItem(SOURCE_KEY, source)
  } catch {
    return
  }
  notify()
}

export const clearStoredReferralCode = (onlyCode?: string) => {
  const stored = readStoredReferralCode()
  if (stored === null || (onlyCode !== undefined && normalizeReferralCode(onlyCode) !== stored)) return
  try {
    clearRef()
    localStorage.removeItem(SOURCE_KEY)
  } catch {
    return
  }
  notify()
}

export const markReferralCodeTyped = () => {
  write(TYPED_OWNER_KEY, viewer)
  write(SOURCE_KEY, "typed")
}

export const clearReferralCodeTyped = () => {
  if (read(SOURCE_KEY) !== "typed") return
  write(SOURCE_KEY, null)
  write(TYPED_OWNER_KEY, null)
}

const isTyped = () => read(SOURCE_KEY) === "typed"

export const captureLinkReferralCode = () => {
  if (typeof window === "undefined" || isTyped()) return
  const code = normalizeReferralCode(new URLSearchParams(window.location.search).get(REF_PARAM))
  if (code) storeReferralCode(code, "link")
}

export const syncHostReferralCode = () => {
  const declared = hostReferralCode()
  if (isTyped()) return
  if (declared === null) {
    if (read(SOURCE_KEY) === "host") clearStoredReferralCode()
    return
  }
  if (typeof declared !== "string") return
  const code = normalizeReferralCode(declared)
  if (!code) return
  if (read(HOST_SPENT_KEY) === code) return
  const stored = readStoredReferralCode()
  if (stored === code) return
  if (stored !== null && read(SOURCE_KEY) !== "host") return
  if (parseReferralApplied(read(APPLIED_KEY))?.code.toUpperCase() === code) return
  const rejection = parseReferralRejection(read(REJECTION_KEY))
  if (rejection?.code.toUpperCase() === code && isTerminalReferralRejection(rejection.reason)) return
  storeReferralCode(code, "host")
}

export const syncReferralAccount = (nextViewer: string) => {
  viewer = nextViewer
  if (read(HOST_SPENT_KEY) !== null && read(HOST_SPENT_OWNER_KEY) !== viewer) {
    write(HOST_SPENT_KEY, null)
    write(HOST_SPENT_OWNER_KEY, null)
    syncHostReferralCode()
  }
  if (!isTyped() || read(TYPED_OWNER_KEY) === viewer) return
  clearReferralCodeTyped()
  captureLinkReferralCode()
  syncHostReferralCode()
}

export const spendHostReferralCode = (onlyCode?: string) => {
  const declared = hostReferralCode()
  const code = typeof declared === "string" ? normalizeReferralCode(declared) : null
  if (!code || (onlyCode !== undefined && normalizeReferralCode(onlyCode) !== code)) return
  write(HOST_SPENT_OWNER_KEY, viewer)
  write(HOST_SPENT_KEY, code)
}

const isFresh = (parsed: object) => "at" in parsed && typeof parsed.at === "number" && Date.now() - parsed.at < NOTICE_TTL_MS

export const rememberReferralRejection = (rejection: ReferralRejection) => write(REJECTION_KEY, JSON.stringify({ ...rejection, at: Date.now() }))

export const readReferralRejectionRaw = (): string | null => {
  const raw = read(REJECTION_KEY)
  return parseReferralRejection(raw) ? raw : null
}

const isRejection = (value: unknown): value is PromoRejection => PROMO_REJECTIONS.some((reason) => reason === value)

export const parseReferralRejection = (raw: string | null): ReferralRejection | null => {
  if (!raw) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    if (parsed && typeof parsed === "object" && isFresh(parsed) && "code" in parsed && "reason" in parsed && typeof parsed.code === "string" && isRejection(parsed.reason)) {
      return { code: parsed.code, reason: parsed.reason, ...("dropped" in parsed && parsed.dropped === true ? { dropped: true } : {}) }
    }
  } catch {
    return null
  }
  return null
}

export const clearReferralRejection = () => {
  if (read(REJECTION_KEY) !== null) write(REJECTION_KEY, null)
}

export const rememberReferralApplied = (applied: ReferralApplied) => write(APPLIED_KEY, JSON.stringify({ ...applied, at: Date.now() }))

export const readReferralAppliedRaw = (): string | null => {
  const raw = read(APPLIED_KEY)
  return parseReferralApplied(raw) ? raw : null
}

export const parseReferralApplied = (raw: string | null): ReferralApplied | null => {
  if (!raw) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    if (parsed && typeof parsed === "object" && isFresh(parsed) && "code" in parsed && "amount" in parsed && typeof parsed.code === "string" && typeof parsed.amount === "number") {
      return { code: parsed.code, amount: parsed.amount }
    }
  } catch {
    return null
  }
  return null
}

export const clearReferralApplied = () => {
  if (read(APPLIED_KEY) !== null) write(APPLIED_KEY, null)
}

export const readReferralInviteDismissed = (): string | null => read(BANNER_DISMISSED_KEY)

export const dismissReferralInvite = (code: string) => write(BANNER_DISMISSED_KEY, code)

export const orderReferralSeen = (cartId: string): boolean => read(ORDER_SEEN_KEY) === cartId

export const markOrderReferralSeen = (cartId: string) => write(ORDER_SEEN_KEY, cartId)

export const clearReferralNotices = () => {
  clearReferralRejection()
  clearReferralApplied()
}

export const clearReferralAttribution = () => {
  clearStoredReferralCode()
  clearReferralNotices()
  if (read(SOURCE_KEY) !== null) write(SOURCE_KEY, null)
  if (read(TYPED_OWNER_KEY) !== null) write(TYPED_OWNER_KEY, null)
  if (read(BANNER_DISMISSED_KEY) !== null) write(BANNER_DISMISSED_KEY, null)
}

export const subscribeReferralAttribution = (listener: () => void) => {
  window.addEventListener(CHANGE_EVENT, listener)
  window.addEventListener("storage", listener)
  return () => {
    window.removeEventListener(CHANGE_EVENT, listener)
    window.removeEventListener("storage", listener)
  }
}
